import { get, deleteEntity, post } from 'services';
import { ApiController } from 'config';

type PlusOneResponse = {
  plusOneEmail: string;
  name: string;
  plusOneAccessTerminationDate: string;
  active: boolean;
};

type TopPerformerResponse = {
  id: number;
  login: string;
  name: string;
  topPerformerStartDate: string;
  topPerformerEndDate: string;
  invitesCount: number;
  removalsCount: number;
  plusOne: PlusOneResponse | null;
};

type HistoryItemResponse = {
  id: number;
  guestEmail: string;
  guestFirstName: string;
  guestLastName: string;
  status: string;
  invitationDate: string;
  terminationDate: string;
};

export type APlayerUser = {
  id: string;
  name: string;
  username: string;
  status: string;
  membershipStart: string;
  membershipEnd: string;
};

export type InvitationInfo = {
  invitesUsedThisQuarter: number;
  invitesAllowedPerQuarter: number;
  activeGuestExists: boolean;
};

export type ActiveGuest = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  membershipStart: string;
  membershipEnd: string;
  status: string;
};

export type PlusOneGuest = {
  plusOneEmail: string;
  name: string;
  plusOneAccessTerminationDate: string;
  active: boolean;
};

export type APlayerState = {
  user: APlayerUser;
  invitationInfo: InvitationInfo;
  activeGuest: PlusOneGuest | null;
  history: ActiveGuest[];
};

const TOP_PERFORMER_BASE = 'top-performers';
const INVITES_ALLOWED_PER_QUARTER = 2;

const mapHistoryItemToActiveGuest = (
  item: HistoryItemResponse,
): ActiveGuest => ({
  id: String(item.id),
  firstName: item.guestFirstName,
  lastName: item.guestLastName,
  email: item.guestEmail,
  membershipStart: item.invitationDate?.slice(0, 10) ?? '',
  membershipEnd: item.terminationDate?.slice(0, 10) ?? '',
  status: item.status,
});

export const getAPlayerState = async (
  userId: number,
): Promise<APlayerState> => {
  const [performer, history] = await Promise.all([
    get<TopPerformerResponse>({ url: `/${TOP_PERFORMER_BASE}/${userId}` }),
    get<HistoryItemResponse[]>({
      url: `/${ApiController.invitations}/history`,
    }),
  ]);

  return {
    user: {
      id: String(performer.id),
      name: performer.name,
      username: performer.login,
      status: 'TOP_PERFORMER',
      membershipStart: performer.topPerformerStartDate,
      membershipEnd: performer.topPerformerEndDate,
    },
    invitationInfo: {
      invitesUsedThisQuarter: performer.invitesCount,
      invitesAllowedPerQuarter: INVITES_ALLOWED_PER_QUARTER,
      activeGuestExists: performer.plusOne?.active ?? false,
    },
    activeGuest: performer.plusOne ?? null,
    history: history
      .filter((item) => item.status !== 'ACTIVE')
      .map(mapHistoryItemToActiveGuest),
  };
};

export const inviteGuest = (data: {
  firstName: string;
  lastName: string;
  email: string;
}) => post<void>({ url: `/${ApiController.invitations}`, body: data });

export const removeGuest = () =>
  deleteEntity<void>(`/${ApiController.invitations}`);
